import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabaseAnalyticsService } from '@/services/supabaseAnalyticsService';

export interface UseEventTrackingResult {
  trackPageView: (path?: string, eventId?: string) => void;
  trackEventView: (eventId: string) => void;
  trackEventClick: (eventId: string, clickType?: string) => void;
  trackCustomEvent: (eventType: string, eventData?: Record<string, any>) => void;
}

export const useEventTracking = (): UseEventTrackingResult => {
  const location = useLocation();

  // Initialize session tracking on first mount

  // Track page changes only for event pages
  useEffect(() => {
    const trackPageChange = async () => {
      // Only track if URL contains /event/
      if (location.pathname.startsWith('/event/')) {
        // Extract event ID from URL if possible
        const pathParts = location.pathname.split('/');
        const eventIdentifier = pathParts[2]; // /event/{id}

        await supabaseAnalyticsService.trackPageView(location.pathname, eventIdentifier);
      }
    };

    trackPageChange();
  }, [location.pathname]);

  // Manual tracking functions
  const trackPageView = useCallback(async (path?: string, eventId?: string) => {
    const pagePath = path || location.pathname;
    // Only track event pages
    if (pagePath.startsWith('/event/')) {
      await supabaseAnalyticsService.trackPageView(pagePath, eventId);
    }
  }, [location.pathname]);

  // Helper function to extract numeric ID from URL or return as-is
  const extractEventId = (eventId: string): string => {
    // If it's a URL path like "/occasions/745239", extract the number
    if (eventId.includes('/occasions/')) {
      const match = eventId.match(/\/occasions\/(\d+)/);
      return match ? match[1] : eventId;
    }
    // If it's already numeric or UUID, return as-is
    return eventId;
  };

  // Track event views - no restrictions, track all event views
  const trackEventView = useCallback(async (eventId: string) => {
    if (!eventId) {
      console.warn('trackEventView called with invalid eventId:', eventId);
      return;
    }
    const cleanEventId = extractEventId(eventId);
    console.log('Tracking event view for eventId:', cleanEventId, 'from original:', eventId);
    try {
      await supabaseAnalyticsService.trackEventView(cleanEventId);
    } catch (error) {
      console.error('Error tracking event view:', error);
    }
  }, []);

  // Track event clicks - no restrictions, track all event clicks
  const trackEventClick = useCallback(async (eventId: string, clickType: string = 'general') => {
    if (!eventId) {
      console.warn('trackEventClick called with invalid eventId:', eventId);
      return;
    }
    const cleanEventId = extractEventId(eventId);
    console.log('Tracking event click for eventId:', cleanEventId, 'from original:', eventId, 'clickType:', clickType);
    try {
      await supabaseAnalyticsService.trackEventClick(cleanEventId, clickType);
    } catch (error) {
      console.error('Error tracking event click:', error);
    }
  }, []);

  const trackCustomEvent = useCallback(async (eventType: string, eventData?: Record<string, any>) => {
    // Only track custom events on event pages
    if (location.pathname.startsWith('/event/')) {
      await supabaseAnalyticsService.trackEvent({
        event_type: eventType,
        event_data: eventData,
        page_path: location.pathname,
      });
    }
  }, [location.pathname]);

  return {
    trackPageView,
    trackEventView,
    trackEventClick,
    trackCustomEvent,
  };
};

export default useEventTracking;